import { Injectable } from '@angular/core';
import { BaseService } from '../base/base.service';
import { Observable } from 'rxjs';
import { filter, map, take } from 'rxjs/operators';
import { DunningProcess, DunningProcessList } from './dunning_process';
import { HttpClient } from '@angular/common/http';
import { DunningProcessSerializer } from './dunning_process.serializer';
import { environment } from '../../environments/environment';
import { DunningMail } from './dunning-mail';
import { ShipmentReceivers } from '../shipments/shipment';

export interface TerminatePrepareResult {
  invoice_id: number;
  invoice_number: string;
  remaining_months: number;
  shipment_id: number | null;
  shipment_parcel_number: string | null;
  shipment_warning: string | null;
}

export interface FinalInvoiceDefaults {
  suggested_months: number | null;
  contract_expires_at: string | null;
  client_number: string;
  contract_id: number | null;
  contract_number: string | null;
  contract_signed_on: string | null;
  period_start: string | null;
  has_base_invoice: boolean;
  monthly_netto_cents: number;
  vat_rate: number;
  unpaid_count: number;
  unpaid_netto_sum_cents: number;
}

@Injectable({
  providedIn: 'root'
})
export class DunningProcessService extends BaseService<DunningProcess, DunningProcessList> {
  constructor(http: HttpClient) {
    super(http);
    this.endpoint = 'dunning_processes';
    this.serializer = new DunningProcessSerializer();
  }

  note(id: number, note: string): Observable<DunningProcess> {
    return this.http.post(`${environment.base_url + this.endpoint}/${id}/note`, {'note': note}).pipe(
      filter(x => !!x),
      take(1),
      map(res => this.serializer.fromJson(res))
    );
  }

  dunningMail(id: number, dunningMail: DunningMail): Observable<void> {
    return this.http.post<void>(`${environment.base_url + this.endpoint}/${id}/dunning_mail`, {dunning_mail: dunningMail});
  }

  terminationMail(id: number, ids: { invoice_id: number, shipment_id?: number, contract_id?: number },
                  mail: { title: string, text: string, letter_title: string, letter_text: string }): Observable<void> {
    return this.http.post<void>(`${environment.base_url + this.endpoint}/${id}/termination_mail`,
      {...ids, termination_mail: mail});
  }

  finalInvoiceDefaults(id: number): Observable<FinalInvoiceDefaults> {
    return this.http.get<FinalInvoiceDefaults>(`${environment.base_url + this.endpoint}/${id}/final_invoice_defaults`);
  }

  createFinalInvoice(id: number, remainingMonths: number, discountPercent?: number,
                     discountDescription?: string): Observable<{ id: number, invoice_number: string }> {
    return this.http.post<{ id: number, invoice_number: string }>(
      `${environment.base_url + this.endpoint}/${id}/create_final_invoice`,
      {
        final_invoice: {
          remaining_months: remainingMonths,
          discount_percent: discountPercent,
          discount_description: discountDescription
        }
      });
  }

  terminatePrepare(id: number, remainingMonths?: number,
                   receiverId: string = ShipmentReceivers.returns01): Observable<TerminatePrepareResult> {
    return this.http.post<TerminatePrepareResult>(
      `${environment.base_url + this.endpoint}/${id}/terminate_prepare`,
      {remaining_months: remainingMonths, receiver_id: receiverId});
  }

  claimInvoicePackage(id: number): Observable<Blob> {
    return this.http.post(`${environment.base_url + this.endpoint}/${id}/claim_invoice_package`, {},
      {responseType: 'blob'});
  }

  terminationPackage(id: number, ids: { invoice_id: number, shipment_id?: number },
                     pkg: { title: string, text: string }): Observable<Blob> {
    return this.http.post(`${environment.base_url + this.endpoint}/${id}/termination_package`,
      {...ids, termination_package: pkg},
      {responseType: 'blob'});
  }

  finalInvoice(id: number, remainingMonths: number, discountPercent?: number, discountDescription?: string): Observable<Blob> {
    return this.http.post(`${environment.base_url + this.endpoint}/${id}/final_invoice`,
      {
        final_invoice: {
          remaining_months: remainingMonths,
          discount_percent: discountPercent,
          discount_description: discountDescription
        }
      },
      {responseType: 'blob'});
  }

  openCases(): Observable<OpenCase[]> {
    return this.http.get<{ data: OpenCase[] }>(`${environment.base_url + this.endpoint}/open_cases`)
      .pipe(map(res => res.data));
  }

  monthInvoices(month: string): Observable<MonthInvoices> {
    return this.http.get<MonthInvoices>(
      `${environment.base_url + this.endpoint}/month_invoices`, {params: {month}}
    );
  }
}

export interface OpenCase {
  id: number;
  client: { id: number, name: string };
  process_level: string;
  open_cents: number;
  open_count: number;
  oldest_due_date: string | null;
  days_overdue: number | null;
  started_at: string;
  days_open: number;
  last_action_at: string | null;
  days_since_action: number | null;
  due_at: string | null;
}

export interface MonthInvoices {
  data: MonthInvoice[];
  recovered: ClosedInvoice[];
  lost: ClosedInvoice[];
}

export interface MonthInvoice {
  id: number;
  invoice_number: string;
  brutto_cents: number;
  due_date: string | null;
  days_overdue: number | null;
  client: { id: number, name: string };
  payment_method: 'sepa' | 'transfer' | 'credit_note';
  dunning_process: { id: number, process_level: string, due_at: string | null };
}

export interface ClosedInvoice {
  id: number;
  invoice_number: string;
  brutto_cents: number;
  client: { id: number, name: string };
  payment_method: 'sepa' | 'transfer' | 'credit_note';
  closed_at: string;
  closed_level: string | null;
  reason: 'paid' | 'written_off' | 'replaced';
}
